<template>
  <div class="radioGroup" :class="extraClass" :data-private-info="true">
    <var-style-provider :style-vars="styleVars">
      <var-radio-group
        ref="radioGroupRef"
        v-model="modelValue"
        :name="name"
        :label="label"
        :options="options"
        :direction="direction"
        :rules="validateInput"
        @change="onInputChange"
        :data-testid="radioGroupTestId"
      >
      </var-radio-group>
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import './RadioGroup.scss';
import type { LDTheme } from 'ewi-ui-helpers/types';

defineOptions({
  inheritAttrs: false,
});

export type RadioGroupOption = {
  value: string;
  label?: string;
  disabled?: boolean;
};

export interface RadioGroupProps {
  modelValue: string | undefined;
  defaultValue?: string;
  name: string;
  label?: string;
  options: RadioGroupOption[];
  direction?: 'horizontal' | 'vertical';
  theme?: LDTheme;
  extraClass?: string;
  componentId?: string;
  radioGroupTestId?: string;
  validateInput?: (value: string) => string | boolean;
}

const props = withDefaults(defineProps<RadioGroupProps>(), {
  componentId: '',
  radioGroupTestId: '',
  theme: 'light',
  extraClass: '',
});

const emit = defineEmits(['update:modelValue']);

const radioGroupRef = ref();

const lightTheme = {
  '--radio-checked-color': 'var(--SysLightPrimary)',
  '--radio-unchecked-color': 'var(--SysLightOnSurfaceVariant)',
  '--radio-text-color': 'var(--SysLightOnSurfaceVariant)',
  '--radio-disabled-color': 'var(--SysLightOnSurface)',
};

const darkTheme = {
  '--radio-checked-color': 'var(--SysDarkPrimary)',
  '--radio-unchecked-color': 'var(--SysDarkOnSurfaceVariant)',
  '--radio-text-color': 'var(--SysDarkOnSurfaceVariant)',
  '--radio-disabled-color': 'var(--SysDarkOnSurface)',
};

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);

const modelValue = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val);
  },
});

onMounted(() => {
  // console.log('onmounted', radioGroupRef.value);
  if (props.defaultValue && !props.modelValue) {
    emit('update:modelValue', props.defaultValue);
  }
});

onMounted(() => {
  if (props.options.length > 0) {
    const radioActionEl =
      radioGroupRef.value?.$el?.querySelector('[tabindex="0"]');
    radioActionEl?.removeAttribute('tabindex');

    const radioWrapperEl =
      radioGroupRef.value?.$el?.querySelector('[role="radio"]');
    radioWrapperEl?.setAttribute('tabindex', '0');
  }
});

const validateInput = (value: string) => {
  if (props.validateInput) {
    return props.validateInput(value);
  }
  return true;
};
// todo remove completely?
const onInputChange = (_value: string) => {
  // console.log('onInputChange', value);
  // emit('update:modelValue', value);
};

defineExpose({
  validate: async () => {
    if (radioGroupRef.value) {
      const isValid = await radioGroupRef.value.validate();
      // console.log('radio group ref isValid', isValid);
      return isValid;
    } else {
      // console.log('ref not available', modelValue.value);
    }
    return false;
  },
});
</script>
